/**
 * Rutas públicas del tracker, con segmento localizado por mercado.
 *
 * Los segmentos son los que se buscan en cada idioma ("dónde está papá noel",
 * "where is santa", "onde está o papai noel"), no una traducción literal del
 * español. Deben coincidir con `routing.pathnames` en `i18n/routing.ts`.
 */

import { getLocalePrefix, hrefForSegment, type PublicLocale } from '@/lib/locale-prefix'
import { TRACKER_LOCALES } from './types'

/**
 * La lista vive en `types.ts`, que es el contrato compartido por rutas y
 * contenido; este módulo la reexporta porque es el entry point de navegación.
 */
export { TRACKER_LOCALES }

/** Segmento del tracker por locale (sin barra inicial). */
export const TRACKER_PATH_BY_LOCALE: Record<PublicLocale, string> = {
  es: 'donde-esta-papa-noel',
  en: 'where-is-santa',
  pt: 'onde-esta-o-papai-noel',
  it: 'dov-e-babbo-natale',
  fr: 'ou-est-le-pere-noel',
  ja: 'santa-wa-doko',
  de: 'wo-ist-der-weihnachtsmann',
}

/** Href del tracker en el locale indicado.
 *
 *  Los locales publicados (`TRACKER_LOCALES`) resuelven su segmento
 *  localizado; un locale desconocido normaliza a es por `hrefForSegment`
 *  (fallback histórico). El gating a la home del locale existió mientras
 *  todos los mercados publicados resuelven su segmento; la rama de fallback
 *  queda reservada para un locale que aún no esté publicado.
 */
export function getSantaTrackerHref(locale: string): string {
  if (!(TRACKER_LOCALES as readonly string[]).includes(locale)) {
    return getLocalePrefix(locale) || '/'
  }
  return hrefForSegment(TRACKER_PATH_BY_LOCALE, locale)
}

/** URL absoluta del tracker, para canonical, hreflang y compartir. */
export function getSantaTrackerUrl(locale: string, appUrl: string): string {
  return `${appUrl}${getSantaTrackerHref(locale)}`
}

/** Prefijo de locale reexportado para las páginas que ya lo necesitan. */
export { getLocalePrefix }
